/**
 * Cumple Sprint 16 — Página de actas de reunión.
 *
 * ISO 19011:2018 §6.4.2 (opening) y §6.4.10 (closing). Permite al
 * auditor capturar asistentes, agenda y notas antes de empezar
 * fieldwork (opening) y al cerrar (closing).
 *
 * URL: /assessments/:id/meetings
 */
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import {
  type Attendee,
  type AuditMeetingRecord,
  type MeetingKind,
  auditMeetingsApi,
} from '../api/audit-meetings.js';
import { Card, LoadingSkeleton, PageHeader } from '../components/ui.js';

interface MeetingFormState {
  attendees: Attendee[];
  agenda: string;
  notes: string;
  heldAt: string;
}

const EMPTY_FORM: MeetingFormState = {
  attendees: [{ name: '', role: '' }],
  agenda: '',
  notes: '',
  heldAt: new Date().toISOString().slice(0, 16),
};

const KIND_META: Record<MeetingKind, { title: string; ref: string; hint: string }> = {
  opening: {
    title: 'Reunión de apertura',
    ref: 'ISO 19011:2018 §6.4.2',
    hint: 'Confirma objetivos, alcance, criterios, plan de auditoría con la parte auditada.',
  },
  closing: {
    title: 'Reunión de cierre',
    ref: 'ISO 19011:2018 §6.4.10',
    hint: 'Presenta hallazgos, conclusiones, próximos pasos y compromisos de remediación.',
  },
};

export function AuditMeetingsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBackTo } = useAppShell();
  const [loading, setLoading] = useState(true);

  // Back: vuelve al levantamiento (capture o closed según el status).
  // Sin acceso al status aquí, usamos capture como fallback conservador (BB-UX-01).
  useEffect(() => {
    if (!id) return;
    setBackTo({ href: `/assessments/${id}/capture`, label: 'Levantamiento' });
    return () => setBackTo(null);
  }, [setBackTo, id]);
  const [forms, setForms] = useState<Record<MeetingKind, MeetingFormState>>({
    opening: EMPTY_FORM,
    closing: EMPTY_FORM,
  });
  const [existing, setExisting] = useState<Partial<Record<MeetingKind, AuditMeetingRecord>>>({});
  const [savingKind, setSavingKind] = useState<MeetingKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    auditMeetingsApi
      .list(id)
      .then((d) => {
        if (cancelled) return;
        const byKind: Partial<Record<MeetingKind, AuditMeetingRecord>> = {};
        const nextForms: Record<MeetingKind, MeetingFormState> = {
          opening: EMPTY_FORM,
          closing: EMPTY_FORM,
        };
        for (const m of d.meetings) {
          byKind[m.kind] = m;
          nextForms[m.kind] = {
            attendees: m.attendees.length > 0 ? m.attendees : [{ name: '', role: '' }],
            agenda: m.agenda ?? '',
            notes: m.notes ?? '',
            heldAt: m.held_at.slice(0, 16),
          };
        }
        setExisting(byKind);
        setForms(nextForms);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error cargando');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  function updateForm(kind: MeetingKind, patch: Partial<MeetingFormState>): void {
    setForms((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));
  }

  function updateAttendee(
    kind: MeetingKind,
    idx: number,
    field: keyof Attendee,
    value: string,
  ): void {
    setForms((prev) => {
      const list = [...prev[kind].attendees];
      list[idx] = { ...list[idx]!, [field]: value };
      return { ...prev, [kind]: { ...prev[kind], attendees: list } };
    });
  }

  function addAttendee(kind: MeetingKind): void {
    setForms((prev) => ({
      ...prev,
      [kind]: { ...prev[kind], attendees: [...prev[kind].attendees, { name: '', role: '' }] },
    }));
  }

  function removeAttendee(kind: MeetingKind, idx: number): void {
    setForms((prev) => {
      const list = prev[kind].attendees.filter((_, i) => i !== idx);
      return {
        ...prev,
        [kind]: {
          ...prev[kind],
          attendees: list.length > 0 ? list : [{ name: '', role: '' }],
        },
      };
    });
  }

  async function submit(e: FormEvent, kind: MeetingKind): Promise<void> {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSavingKind(kind);
    try {
      const form = forms[kind];
      const attendees = form.attendees.filter((a) => a.name.trim() && a.role.trim());
      if (attendees.length === 0) {
        setError('Al menos un asistente con nombre y rol es obligatorio');
        return;
      }
      const result = await auditMeetingsApi.upsert(id, kind, {
        attendees,
        agenda: form.agenda.trim() || null,
        notes: form.notes.trim() || null,
        held_at: new Date(form.heldAt).toISOString(),
      });
      setExisting((prev) => ({ ...prev, [kind]: result.meeting }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error guardando acta');
    } finally {
      setSavingKind(null);
    }
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="ACTAS DE AUDITORÍA"
        title="Reuniones de apertura y cierre"
        subtitle="ISO 19011:2018 §6.4 — registro formal de las reuniones obligatorias con la parte auditada"
      />

      {error && (
        <div
          role="alert"
          className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      {loading && <LoadingSkeleton rows={4} />}

      {!loading &&
        (['opening', 'closing'] as MeetingKind[]).map((kind) => {
          const meta = KIND_META[kind];
          const form = forms[kind];
          const exists = !!existing[kind];
          return (
            <Card
              key={kind}
              title={meta.title}
              hint={`${meta.ref} · ${meta.hint}${exists ? ' · Acta guardada — editable' : ''}`}
            >
              <form
                onSubmit={(e) => {
                  void submit(e, kind);
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`held_at_${kind}`}
                      className="block text-xs font-semibold text-slate-700 mb-1"
                    >
                      Fecha y hora <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id={`held_at_${kind}`}
                      type="datetime-local"
                      required
                      value={form.heldAt}
                      onChange={(e) => updateForm(kind, { heldAt: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <p className="block text-xs font-semibold text-slate-700 mb-1">
                    Asistentes <span className="text-rose-500">*</span>
                  </p>
                  <div className="space-y-2">
                    {form.attendees.map((a, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2">
                        <input
                          type="text"
                          placeholder="Nombre"
                          value={a.name}
                          onChange={(e) => updateAttendee(kind, idx, 'name', e.target.value)}
                          className="col-span-4 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Rol / cargo"
                          value={a.role}
                          onChange={(e) => updateAttendee(kind, idx, 'role', e.target.value)}
                          className="col-span-4 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Organización (opcional)"
                          value={a.organization ?? ''}
                          onChange={(e) =>
                            updateAttendee(kind, idx, 'organization', e.target.value)
                          }
                          className="col-span-3 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttendee(kind, idx)}
                          aria-label="Eliminar asistente"
                          className="col-span-1 text-sm text-rose-600 hover:text-rose-700"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => addAttendee(kind)}
                    className="mt-2 text-xs font-semibold text-brand-700 hover:text-brand-800"
                  >
                    + Añadir asistente
                  </button>
                </div>

                <div>
                  <label
                    htmlFor={`agenda_${kind}`}
                    className="block text-xs font-semibold text-slate-700 mb-1"
                  >
                    Agenda
                  </label>
                  <textarea
                    id={`agenda_${kind}`}
                    rows={3}
                    value={form.agenda}
                    onChange={(e) => updateForm(kind, { agenda: e.target.value })}
                    placeholder={
                      kind === 'opening'
                        ? 'Ej. Presentación equipo, confirmación de alcance, plan de visita, recordatorio de confidencialidad'
                        : 'Ej. Resumen de hallazgos, NC mayor/menor identificados, próximos pasos, plazos de remediación'
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor={`notes_${kind}`}
                    className="block text-xs font-semibold text-slate-700 mb-1"
                  >
                    Notas / acuerdos
                  </label>
                  <textarea
                    id={`notes_${kind}`}
                    rows={4}
                    value={form.notes}
                    onChange={(e) => updateForm(kind, { notes: e.target.value })}
                    placeholder="Acuerdos puntuales, decisiones, observaciones de la parte auditada…"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingKind === kind}
                    className="rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {savingKind === kind
                      ? 'Guardando…'
                      : exists
                        ? `Actualizar acta de ${kind === 'opening' ? 'apertura' : 'cierre'}`
                        : `Guardar acta de ${kind === 'opening' ? 'apertura' : 'cierre'}`}
                  </button>
                </div>
              </form>
            </Card>
          );
        })}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => navigate(`/assessments/${id ?? ''}/capture`)}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          ← Volver al levantamiento
        </button>
      </div>
    </main>
  );
}
